import { useCallback, useEffect, useRef, useState } from "react";
import { getDeviceBroadcastIceServers } from "@workspace/api-client-react";
import { captureError } from "@workspace/observability/web";
import type { SignalMessage } from "@/player/hooks/use-player-realtime";

/**
 * WebRTC signaling payloads exchanged over the realtime gateway for a live
 * broadcast. The broadcaster (a control peer) sends the offer and ICE
 * candidates; the player answers and trickles its own ICE back. `to` carries
 * the broadcaster's user id so the device's replies (which the gateway fans out
 * to every control peer) are only acted on by the originating broadcaster.
 */
type IncomingPayload =
  | { kind: "offer"; sdp: string; sessionType?: string }
  | { kind: "ice"; candidate: RTCIceCandidateInit }
  | { kind: "end" };

interface UseBroadcastReceiverOptions {
  /** Relay a signaling payload back toward the control peers. */
  sendSignal: (payload: unknown) => void;
  /** Duck/resume the scheduled program while a broadcast is on air. */
  setBroadcastActive: (active: boolean) => void;
  /** Attention tone to play when a live announcement offer arrives (null = off). */
  announcementToneUrl?: string | null;
  /**
   * Effective device output level (0–100) to play broadcast audio at. The
   * attention tone and the inbound live-voice stream are scaled to this so an
   * announcement matches the venue's set level instead of blasting at full
   * volume (CB-145). The caller folds mute in (passing 0 when muted).
   */
  deviceVolume?: number;
}

export interface BroadcastReceiver {
  /** A live broadcast is currently playing through this device. */
  receiving: boolean;
  /** Feed an inbound signaling message from the realtime client. */
  handleSignal: (sig: SignalMessage) => void;
  /** End the broadcast when the server reports presenter disconnect, etc. */
  handleBroadcastEnded: () => void;
}

function isPayload(v: unknown): v is IncomingPayload {
  return typeof v === "object" && v !== null && "kind" in v;
}

/**
 * Receives a live broadcast on a player device. It answers the broadcaster's
 * WebRTC offer (one peer connection per device in the mesh), plays the inbound
 * audio through a dedicated element, and ducks the scheduled program for the
 * duration. ICE servers come from the device-authenticated endpoint (Cloudflare
 * TURN when configured, STUN otherwise). When the broadcast ends — by an `end`
 * signal or the peer connection dropping — the scheduled program resumes.
 */
export function useBroadcastReceiver(
  options: UseBroadcastReceiverOptions,
): BroadcastReceiver {
  const {
    sendSignal,
    setBroadcastActive,
    announcementToneUrl = null,
    deviceVolume = 100,
  } = options;

  const [receiving, setReceiving] = useState(false);
  // Live 0–1 gain for broadcast audio, kept in a ref so the always-current value
  // is applied even though offers arrive outside React's render cycle.
  const broadcastGainRef = useRef(Math.max(0, Math.min(1, deviceVolume / 100)));
  broadcastGainRef.current = Math.max(0, Math.min(1, deviceVolume / 100));
  // Reflect a mid-broadcast volume change onto the live element.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = broadcastGainRef.current;
  }, [deviceVolume]);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const broadcasterRef = useRef<string | null>(null);
  /** Session kind from the offer ("announcement", "broadcast", …), echoed back
   *  on the answer/ICE so the gateway can match the originating live session. */
  const sessionTypeRef = useRef<string>("broadcast");
  /** Server ended the session — ignore late signaling until a fresh offer. */
  const serverEndedRef = useRef(false);

  const sendSignalRef = useRef(sendSignal);
  sendSignalRef.current = sendSignal;
  const setBroadcastActiveRef = useRef(setBroadcastActive);
  setBroadcastActiveRef.current = setBroadcastActive;
  const announcementToneUrlRef = useRef(announcementToneUrl);
  announcementToneUrlRef.current = announcementToneUrl;

  const teardown = useCallback(() => {
    const pc = pcRef.current;
    if (pc) {
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.close();
      pcRef.current = null;
    }
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.srcObject = null;
    }
    broadcasterRef.current = null;
    serverEndedRef.current = false;
    setReceiving(false);
    setBroadcastActiveRef.current(false);
  }, []);

  const handleOffer = useCallback(
    async (from: string, sdp: string, sessionType: string) => {
      serverEndedRef.current = false;
      // A new offer supersedes any prior connection.
      if (pcRef.current) teardown();
      broadcasterRef.current = from;
      sessionTypeRef.current = sessionType;

      // Live announcement: sound the attention tone immediately so it fills
      // the SDP answer/ICE round trip; the operator's voice follows on track.
      if (sessionType === "announcement" && announcementToneUrlRef.current) {
        const tone = new Audio(announcementToneUrlRef.current);
        tone.volume = broadcastGainRef.current;
        void tone.play().catch(() => {
          // Autoplay-blocked tabs surface the same gesture requirement on the
          // live stream itself; a silent tone should not fail the broadcast.
        });
      }

      let iceServers: RTCIceServer[] = [];
      try {
        const res = await getDeviceBroadcastIceServers();
        iceServers = (res.iceServers ?? []) as RTCIceServer[];
      } catch {
        // Fall back to a public STUN server so same-network peers still connect.
        iceServers = [{ urls: "stun:stun.l.google.com:19302" }];
      }

      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;

      pc.onicecandidate = (e) => {
        if (e.candidate && broadcasterRef.current) {
          sendSignalRef.current({
            kind: "ice",
            sessionType: sessionTypeRef.current,
            to: broadcasterRef.current,
            candidate: e.candidate.toJSON(),
          });
        }
      };

      pc.ontrack = (e) => {
        let audio = audioRef.current;
        if (!audio) {
          audio = new Audio();
          audio.autoplay = true;
          audioRef.current = audio;
        }
        audio.volume = broadcastGainRef.current;
        audio.srcObject = e.streams[0] ?? new MediaStream([e.track]);
        void audio.play().catch((err) => {
          // Autoplay can be blocked until the player tab gets a user gesture;
          // worth knowing about in production, so report the real error.
          captureError(err instanceof Error ? err : new Error(String(err)), {
            feature: "live-broadcast",
            stage: "autoplay",
          });
        });
        setReceiving(true);
        setBroadcastActiveRef.current(true);
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        if (
          state === "disconnected" ||
          state === "failed" ||
          state === "closed"
        ) {
          teardown();
        }
      };

      try {
        await pc.setRemoteDescription({ type: "offer", sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendSignalRef.current({
          kind: "answer",
          sessionType: sessionTypeRef.current,
          to: from,
          sdp: answer.sdp,
        });
      } catch (err) {
        captureError(err instanceof Error ? err : new Error(String(err)), {
          feature: "live-broadcast",
          stage: "answer",
        });
      }
    },
    [teardown],
  );

  const handleSignal = useCallback(
    (sig: SignalMessage) => {
      if (serverEndedRef.current) return;
      // Only act on signaling that originates from a control (broadcaster) peer.
      if (sig.fromKind !== "control") return;
      if (!isPayload(sig.payload)) return;
      const payload = sig.payload;

      switch (payload.kind) {
        case "offer":
          void handleOffer(sig.from, payload.sdp, payload.sessionType ?? "broadcast");
          return;
        case "ice": {
          // Ignore ICE from any peer other than the active broadcaster.
          if (broadcasterRef.current && sig.from !== broadcasterRef.current) {
            return;
          }
          void pcRef.current?.addIceCandidate(payload.candidate).catch(() => {
            /* candidate may arrive before remote description; safe to drop */
          });
          return;
        }
        case "end":
          if (!broadcasterRef.current || sig.from === broadcasterRef.current) {
            teardown();
          }
          return;
      }
    },
    [handleOffer, teardown],
  );

  // Tear down on unmount so a navigating-away device returns to schedule.
  useEffect(() => teardown, [teardown]);

  const handleBroadcastEnded = useCallback(() => {
    serverEndedRef.current = true;
    teardown();
  }, [teardown]);

  return { receiving, handleSignal, handleBroadcastEnded };
}
